import { useEffect, useState } from "react";
import { Loader2, Truck, Wrench, Clock, Gauge, Fuel, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, StatCard, scoreColor } from "@/components/common/ui";

export default function Fleet() {
  const [vehicles, setVehicles] = useState(null);
  const [trips, setTrips] = useState([]);
  const load = () => api.get("/fleet/vehicles").then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
  useEffect(() => { load(); api.get("/fleet/trips").then((r) => setTrips(r.data)).catch(() => {}); }, []);

  const service = async (id) => { try { await api.post(`/fleet/vehicles/${id}/service`); toast.success("Service logged"); load(); } catch (e) { toast.error("Failed"); } };
  if (!vehicles) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const dueCount = vehicles.filter((v) => v.maintenance_due).length;
  const lateCount = trips.filter((t) => t.status === "late").length;
  const avgUtil = Math.round(vehicles.reduce((a, v) => a + v.utilization, 0) / (vehicles.length || 1));
  const S = { active: "#12B76A", idle: "#F79009", "in-shop": "#F04438" };

  return (
    <div>
      <PageHeader overline="Operations · Module 1 · Fleet" title="Fleet Operations"
        description="Vehicles, trips, utilization and AI predictive maintenance for resident transportation." />
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={Truck} label="Active Vehicles" value={vehicles.filter((v) => v.status === "active").length} accent="#12B76A" index={0} />
        <StatCard icon={Wrench} label="Maintenance Due" value={dueCount} accent="#F04438" index={1} />
        <StatCard icon={Clock} label="Late Trips" value={lateCount} accent="#F79009" index={2} />
        <StatCard icon={Gauge} label="Avg Utilization" value={`${avgUtil}%`} accent="#155EEF" index={3} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {vehicles.map((v) => (
          <div key={v.id} data-testid={`vehicle-${v.id}`} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-semibold"><Truck size={16} className="text-[#155EEF]" /> {v.name}</div>
              <span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: S[v.status], backgroundColor: S[v.status] + "1a" }}>{v.status}</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{v.program_name} · {v.plate} · {v.assigned_driver}</div>
            <div className="mt-3 space-y-2 text-xs">
              <div>
                <div className="mb-0.5 flex justify-between"><span className="flex items-center gap-1 text-muted-foreground"><Fuel size={12} /> Fuel</span><span className="font-semibold">{v.fuel_pct}%</span></div>
                <div className="h-1.5 w-full rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${v.fuel_pct}%`, backgroundColor: scoreColor(v.fuel_pct) }} /></div>
              </div>
              <div>
                <div className="mb-0.5 flex justify-between"><span className="flex items-center gap-1 text-muted-foreground"><Gauge size={12} /> Utilization</span><span className="font-semibold">{v.utilization}%</span></div>
                <div className="h-1.5 w-full rounded-full bg-muted"><div className="h-full rounded-full bg-[#155EEF]" style={{ width: `${v.utilization}%` }} /></div>
              </div>
              <div className="flex justify-between text-muted-foreground"><span>{v.odometer.toLocaleString()} mi · {v.mpg} mpg</span><span>Svc: {v.next_service_date}</span></div>
            </div>
            {v.maintenance_due && (
              <button data-testid={`service-${v.id}`} onClick={() => service(v.id)}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md bg-[#F04438] py-2 text-xs font-semibold text-white transition-colors hover:bg-[#D92D20]">
                <Wrench size={13} /> Predicted maintenance due — Log Service</button>
            )}
          </div>
        ))}
      </div>

      <h2 className="mb-3 mt-8 text-lg font-bold tracking-tight">Recent Trips</h2>
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-4 py-3">Vehicle</th><th className="px-4 py-3">Purpose</th><th className="px-4 py-3">Driver</th><th className="px-4 py-3">Miles</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody className="divide-y">
            {trips.slice(0, 12).map((t) => (
              <tr key={t.id} className="hover:bg-muted/40"><td className="px-4 py-3 font-medium">{t.vehicle_name}</td>
                <td className="px-4 py-3"><span className="flex items-center gap-1 text-muted-foreground"><MapPin size={12} />{t.purpose}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t.driver}</td><td className="px-4 py-3">{t.miles}</td><td className="px-4 py-3 text-muted-foreground">{t.date}</td>
                <td className="px-4 py-3"><span className="rounded-full px-2 py-0.5 text-xs font-semibold capitalize" style={{ color: t.status === "late" ? "#F04438" : t.status === "in-progress" ? "#155EEF" : "#12B76A", backgroundColor: (t.status === "late" ? "#F04438" : t.status === "in-progress" ? "#155EEF" : "#12B76A") + "1a" }}>{t.status}</span></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
